import React from 'react';

/**
 * MoleculeLoader — branded loading indicator inspired by a benzene ring.
 * Six "atom" circles orbit a central nucleus while bonds pulse. SVG-only,
 * no external libs. Respects prefers-reduced-motion.
 *
 * Usage:
 *   <MoleculeLoader label="Loading products…" />            // standalone block
 *   <MoleculeLoader size={32} inline />                     // inside a card / button
 *   <MoleculeLoader label="Loading articles…" tone="amber" />
 */
const tones = {
  teal: { atom: '#0d9488', bond: '#14b8a6', glow: '#5eead4', nucleus: '#134e4a' },
  amber: { atom: '#d97706', bond: '#f59e0b', glow: '#fcd34d', nucleus: '#78350f' },
  slate: { atom: '#475569', bond: '#64748b', glow: '#94a3b8', nucleus: '#0f172a' },
};

const MoleculeLoader = ({
  size = 96,
  label,
  tone = 'teal',
  inline = false,
  className = '',
  'data-testid': testId = 'molecule-loader',
}) => {
  const c = tones[tone] || tones.teal;

  // 6 atoms equally spaced around a circle of radius 36 (viewBox 100×100, center 50,50)
  const radius = 34;
  const cx = 50;
  const cy = 50;
  const atoms = Array.from({ length: 6 }, (_, i) => {
    const angle = (i * Math.PI * 2) / 6;
    return { x: cx + radius * Math.cos(angle), y: cy + radius * Math.sin(angle), i };
  });

  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      data-testid={testId}
      className={
        inline
          ? `inline-flex items-center gap-2 ${className}`
          : `flex flex-col items-center justify-center gap-3 py-12 ${className}`
      }
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 100 100"
        xmlns="http://www.w3.org/2000/svg"
        className="molecule-loader-svg"
        aria-hidden="true"
      >
        <defs>
          <radialGradient id={`mlg-${tone}`} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={c.glow} stopOpacity="0.55" />
            <stop offset="60%" stopColor={c.glow} stopOpacity="0.15" />
            <stop offset="100%" stopColor={c.glow} stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Soft outer halo */}
        <circle cx={cx} cy={cy} r="46" fill={`url(#mlg-${tone})`} className="molecule-halo" />

        {/* Rotating molecule group */}
        <g className="molecule-spin" style={{ transformOrigin: `${cx}px ${cy}px` }}>
          {/* Bonds — adjacent atoms */}
          {atoms.map((a, idx) => {
            const b = atoms[(idx + 1) % atoms.length];
            return (
              <line
                key={`bond-${idx}`}
                x1={a.x}
                y1={a.y}
                x2={b.x}
                y2={b.y}
                stroke={c.bond}
                strokeWidth="2"
                strokeLinecap="round"
                className="molecule-bond"
                style={{ animationDelay: `${idx * 0.15}s` }}
              />
            );
          })}

          {/* Atoms */}
          {atoms.map((a, idx) => (
            <circle
              key={`atom-${idx}`}
              cx={a.x}
              cy={a.y}
              r="6"
              fill={c.atom}
              className="molecule-atom"
              style={{ animationDelay: `${idx * 0.15}s` }}
            />
          ))}
        </g>

        {/* Nucleus — pulses in counter-rhythm */}
        <circle cx={cx} cy={cy} r="4" fill={c.nucleus} className="molecule-nucleus" />
      </svg>

      {label && (
        <p className={inline ? 'text-sm text-slate-600' : 'text-sm text-slate-500 font-medium tracking-wide'}>
          {label}
        </p>
      )}

      {/* Scoped CSS keeps this component self-contained — no external CSS file needed */}
      <style>{`
        @keyframes molSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes molAtomPulse { 0%, 100% { transform: scale(1); opacity: 0.85; } 50% { transform: scale(1.4); opacity: 1; } }
        @keyframes molBondPulse { 0%, 100% { opacity: 0.35; } 50% { opacity: 1; } }
        @keyframes molNucleusPulse { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.6); opacity: 0.55; } }
        @keyframes molHaloPulse { 0%, 100% { opacity: 0.7; } 50% { opacity: 0.35; } }
        .molecule-spin { animation: molSpin 6s linear infinite; transform-box: fill-box; }
        .molecule-atom { animation: molAtomPulse 1.6s ease-in-out infinite; transform-box: fill-box; transform-origin: center; }
        .molecule-bond { animation: molBondPulse 1.6s ease-in-out infinite; }
        .molecule-nucleus { animation: molNucleusPulse 1.4s ease-in-out infinite; transform-box: fill-box; transform-origin: center; }
        .molecule-halo { animation: molHaloPulse 2.4s ease-in-out infinite; transform-box: fill-box; transform-origin: center; }
        @media (prefers-reduced-motion: reduce) {
          .molecule-spin, .molecule-atom, .molecule-bond, .molecule-nucleus, .molecule-halo {
            animation: none;
          }
        }
      `}</style>
    </div>
  );
};

export default MoleculeLoader;
